import {
  HttpClient,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { injectApiUrl } from '@seatix/shared-config';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';

let tokenRequest: Observable<void> | null = null;
const RETRY_CONTEXT = new WeakMap<object, number>();

/**
 * Functional interceptor for handling CSRF token validation and injection
 */
export const ensureCsrfTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const CSRF_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

  const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';
  const tokenExtractor = inject(HttpXsrfTokenExtractor);
  const http = inject(HttpClient);
  const apiUrl = injectApiUrl();
  const csrfPath = 'csrf-cookie';
  const csrfEndpoint = `${apiUrl}/${csrfPath}`;

  if (req.url.endsWith(`/${csrfPath}`)) {
    return next(req);
  }

  if (!CSRF_METHODS.includes(req.method)) {
    return next(req);
  }

  const token = tokenExtractor.getToken();

  if (token) {
    const modifiedReq = req.clone({
      headers: req.headers.set(CSRF_HEADER_NAME, token),
    });

    return next(modifiedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 419 &&
          error.error?.message === 'CSRF token mismatch.'
        ) {
          const retryCount = RETRY_CONTEXT.get(req) || 0;
          const MAX_RETRIES = 1;

          if (retryCount >= MAX_RETRIES) {
            RETRY_CONTEXT.delete(req);
            return throwError(
              () =>
                new Error('CSRF token refresh failed after maximum retries'),
            );
          }

          RETRY_CONTEXT.set(req, retryCount + 1);

          tokenRequest = null;

          return ensureTokenExists(http).pipe(
            switchMap(() => {
              const freshToken = tokenExtractor.getToken();
              if (!freshToken) {
                RETRY_CONTEXT.delete(req);
                return throwError(
                  () => new Error('Failed to obtain fresh CSRF token'),
                );
              }

              const retryReq = req.clone({
                headers: req.headers.set(CSRF_HEADER_NAME, freshToken),
              });

              return next(retryReq).pipe(
                switchMap((response) => {
                  RETRY_CONTEXT.delete(req);
                  return of(response);
                }),
                catchError((retryError) => {
                  RETRY_CONTEXT.delete(req);
                  return throwError(() => retryError);
                }),
              );
            }),
            catchError((refreshError) => {
              RETRY_CONTEXT.delete(req);
              return throwError(() => refreshError);
            }),
          );
        }

        return throwError(() => error);
      }),
    );
  }

  return ensureTokenExists(http).pipe(
    switchMap(() => {
      const newToken = tokenExtractor.getToken();
      if (!newToken) {
        return throwError(() => new Error('Failed to obtain CSRF token'));
      }
      const modifiedReq = req.clone({
        headers: req.headers.set(CSRF_HEADER_NAME, newToken),
      });
      return next(modifiedReq);
    }),
  );

  /**
   * Ensures a CSRF token exists, fetching it if necessary
   */
  function ensureTokenExists(http: HttpClient): Observable<void> {
    if (tokenRequest) {
      return tokenRequest;
    }

    tokenRequest = http.get(csrfEndpoint, { withCredentials: true }).pipe(
      switchMap(() => {
        return of(void 0);
      }),
      catchError((error) => {
        throw error;
      }),
      shareReplay(1),
    );

    tokenRequest.subscribe({
      complete: () => {
        tokenRequest = null;
      },
      error: () => {
        tokenRequest = null;
      },
    });

    return tokenRequest;
  }
};
